import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { buildCourseHref, normalizeProgressPercentage } from "@/utils/course";
import { getLastStudied } from "@/utils/lastStudied";
import { VitrinListedCourse } from "@/models/course/VitrinListedCourse";
import Image from "@/components/image";

const fa = (n: number) => Number(n || 0).toLocaleString("fa-IR");

type Props = {
  course: VitrinListedCourse;
};

function MyCourseCard({ course }: Props) {
  const href = buildCourseHref(course.id, course.mini_app?.uuid);
  // Prefer the small thumbnail — this slot is only 64px, so decoding the
  // full-res cover here is wasted work that janks list render/navigation.
  const thumbnail = course.vitrin_cover_url || course.cover_thumb_url || course.cover_url || null;
  const academy = course.mini_app?.title;

  const progress = useMemo(() => {
    const fromApi = normalizeProgressPercentage(course.progress_percentage);
    if (fromApi !== null) {
      return fromApi;
    }
    const last = getLastStudied();
    if (
      last &&
      String(course.id) === last.courseId &&
      typeof last.progressPercentage === "number"
    ) {
      return Math.max(0, Math.min(100, Math.round(last.progressPercentage)));
    }
    return null;
  }, [course.id, course.progress_percentage]);

  const total = course.lessons_count ?? 0;
  const hasProgress = progress !== null && progress > 0;
  const done = hasProgress ? Math.round((progress! / 100) * total) : 0;

  const certLabel =
    progress !== null && progress >= 100
      ? "تبریک دوره رو تموم کردی"
      : hasProgress
        ? `🎓 ${fa(100 - progress!)}٪ تا پایان دوره`
        : "هنوز شروع نکردی!";

  const metaText = hasProgress
    ? `${fa(done)} از ${fa(total)} جلسه`
    : `${fa(total)} جلسه`;

  return (
    <Link
      to={href}
      className="w-full overflow-hidden rounded-2xl border border-default-200 bg-background"
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className="relative bg-content4 size-16 shrink-0 overflow-hidden rounded-xl border border-default-100"
        >
          {thumbnail ? (
            <Image
              width={64}
              height={64}
              src={thumbnail}
              alt={course.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-1 text-right text-sm font-bold text-default-900">
            {course.title}
          </h3>
          {academy ? (
            <p className="mt-1 text-xs text-default-500">{academy}</p>
          ) : null}
        </div>
      </div>

      <div className="mx-3.5 mb-3.5 rounded-xl bg-content3 p-3">
        <div className="flex items-center justify-between text-xs font-bold">
          <span className="text-primary">{certLabel}</span>
          <span className="text-default-500">{metaText}</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-content4">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-500"
            style={{ width: `${progress ?? 0}%` }}
          />
        </div>
      </div>
    </Link>
  );
}


export default memo(MyCourseCard)